import React, { useState, useEffect } from 'react';
import { useAppContext } from 'context/AppContext';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import strings from 'localization/strings';
import { useLocation, useHistory } from 'react-router-dom';
import { handleResponse } from 'actions/actionHelpers';
import queryString from 'query-string';
import { logIn, setRedirectUrl, setLogInPageMessage } from 'actions/authentication';
import { storeBookingRequestValues, storeBookingRequestValuesAtLoginRedirect } from 'actions/bookingRequest';
import { showSuccessDialog, showErrorDialog } from 'actions/responses';
import rentalTypes from 'enums/rentalTypes';
import { getResizedImageUrl } from 'helpers/ImageHelper';
import { alpha } from '@material-ui/core/styles';
import { convertToNumber } from 'helpers/StringHelper';
import { parseIsoDate } from 'helpers/DateHelper';
import { getStorageTypeItem, sanitizeSelectedCategory, hasDiscountedMonths, isActorEligibleForDiscount, getDiscountMinimumBookingLength } from 'helpers/StorageSiteHelper';
import { getAreaAndPrice } from 'helpers/StorageAreaCalculator';
import { makeStyles } from 'styles/util';
import { getInitialBookingFormValues, validateBookingFormValues, getSelectedStorageGroupId } from 'logic/bookingLogic';
import { googleTagManagerConstants, createGoogleTagManagerUserAction } from 'integration/google-tag-manager/googleTagManagerHelper';
import { makePayment } from 'logic/paymentLogic';
import purchaseTypes from 'enums/purchaseTypes';
import { enhanceStripeErrorMessage } from 'helpers/StripeHelper';
import storageSiteStatuses from 'enums/storageSiteStatuses';
import routes from 'routes';
import { getBusinessOrPrivateName } from 'helpers/ActorHelper';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import PageTitle from 'common/PageTitle';
import OpenGraphImage from 'common/OpenGraphImage';
import StorageSiteFacebookMetaTags from './StorageSiteFacebookMetaTags';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ImageCarousel from './ImageCarousel';
import StorageSiteImage from './StorageSiteImage';
import FullScreenLoader from 'common/FullScreenLoader';
import BookingRequestDialog from './BookingRequestDialog';
import BookingForm from './BookingForm';
import Address from './Address';
import AllStorageSitesForLocation from './AllStorageSitesForLocation';
import StorageSiteMap from './StorageSiteMap';
import DimensionsAndFacts from './DimensionsAndFacts';
import OpeningTimeSpanRules from './OpeningTimeSpanRules';
import ContactOwner from './ContactOwner';
import InitialPaymentDialog from 'account/bookings/InitialPaymentDialog';
import DocumentsAndServices from './DocumentsAndServices';
import Facilities from './Facilities';
import AreaAndPrice from 'common/AreaAndPrice';
import AttentionPolygon from 'common/AttentionPolygon';
import Description from './Description';
import Hidden from '@material-ui/core/Hidden';
import Modal from '@material-ui/core/Modal';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import StorageSiteDetailsTitle from './StorageSiteDetailsTitle';
import StorageSiteBreadcrumbs from './StorageSiteBreadcrumbs';
import Alert from '@material-ui/lab/Alert';
import Container from 'common/Container';
import { Helmet } from 'react-helmet-async';

const useStyles = makeStyles(({ theme, colors }) => ({
    previewContainer: {
        padding: theme.spacing(7, 0),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(0)
        }
    },
    infoWrapper: {
        display: 'flex',
        marginTop: theme.spacing(5),
        gap: theme.spacing(5),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(0, 0, 2, 0),
            flexDirection: 'column'
        }
    },
    imageContainer: {
        marginBottom: theme.spacing(0.5),
        position: 'relative'
    },
    priceContainer: {
        marginBottom: theme.spacing(5)
    },
    bookingFormContainer: {
        minWidth: '300px',
        flex: 1
    },
    infoContainer: {
        flex: '1 0 70%',
        [theme.breakpoints.down('sm')]: {
            flex: '1 0 100%'
        },
        position: 'relative'
    },
    actionButtonContainer: {
        margin: theme.spacing(4, 0),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            padding: theme.spacing(2)
        },
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2, 0)
        },
        '& > *': {
            marginRight: theme.spacing(2),
            fontSize: theme.spacing(2),
            fontStyle: 'normal',
            fontWeight: 'normal',
            lineHeight: '150%',
            letterSpacing: '-0.01em',
            [theme.breakpoints.down('sm')]: {
                marginRight: 0,
                marginBottom: theme.spacing(2),
                '&:last-child': {
                    marginBottom: 0
                }
            }
        }
    },
    ownerName: {
        marginBottom: theme.spacing(2),
        fontStyle: 'italic',
        display: 'block'
    },
    bookingModal: {
        display: 'flex',
        justifyContent: 'center'
    },
    openBookingFormModalButtonContainer: {
        zIndex: '1000',
        display: 'flex',
        justifyContent: 'center',
        position: 'fixed',
        bottom: 0,
        backgroundColor: alpha(colors.secondaryColor, 0.67),
        padding: theme.spacing(2),
        width: '100%',
        '& button': {
            width: '100%',
            minHeight: '50px',
            fontSize: theme.spacing(2),
            fontStyle: 'normal',
            fontWeight: 'normal',
            lineHeight: '150%',
        }
    }
}));

const StorageSiteDetails = ({ storageSite, selectedCategory, isPreview = false }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const querystringParameters = queryString.parse(location.search);
    const requestDialogOpenFromQuerystring = !!(querystringParameters.requestDialogOpen || false);
    const authenticationContext = useSelector(state => state.authentication.context);
    const { user, selectedActor } = authenticationContext;
    const isMobile = !useMediaQuery(theme => theme.breakpoints.up('md'));

    const bookingRequestFormValuesAtLoginRedirect = useSelector(state => {
        const values = { ...state.bookingRequest.formValuesAtLoginRedirect };
        values.requestedStartDate = parseIsoDate(values.requestedStartDate);
        values.requestedEndDate = parseIsoDate(values.requestedEndDate);
        return values;
    });
    const bookingRequestFormValuesFromState = {
        ...useSelector(state => state.bookingRequest.formValues),
        ...(requestDialogOpenFromQuerystring ? bookingRequestFormValuesAtLoginRedirect : undefined)
    };
    const successDialogOpen = useSelector(state => state.responses.successDialogOpen);
    const successDialogData = useSelector(state => state.responses.successDialogData);
    const storageTypeItem = getStorageTypeItem(storageSite.storageType);
    const sanitizedSelectedCategory = sanitizeSelectedCategory(storageSite, selectedCategory);

    const imageOptions = {
        width: 800,
        height: 500,
        mode: 'crop'
    };

    useEffect(() => {
        if(!successDialogOpen && successDialogData?.closeAction) {
            // redirect to booking details page
            history.push(successDialogData?.closeAction);
        }
    }, [successDialogOpen, successDialogData]);

    const imageDisplay = storageSite.images.length >= 1 || storageSite.videos.length >= 1
        ? (
            <ImageCarousel images={storageSite.images} videos={storageSite.videos} width={imageOptions.width} height={imageOptions.height} />
        )
        : (
            <StorageSiteImage
                storageSite={storageSite}
                storageTypeItem={storageTypeItem}
                imageOptions={imageOptions}
            />
        );

    const getDataForBookingRequestDialog = () => {
        const storageGroupId = getSelectedStorageGroupId(bookingRequestFormValuesFromState);
        const storageGroup = storageSite.storageGroups.find(sg => sg.id === storageGroupId);
        const isCustomArea = storageGroup && storageGroup.rentalType === rentalTypes.customArea.key && bookingRequestFormValuesFromState.categories;
        return {
            storageGroup,
            requestedStartDate: bookingRequestFormValuesFromState.requestedStartDate,
            requestedEndDate: bookingRequestFormValuesFromState.requestedEndDate,
            width: isCustomArea ? convertToNumber(bookingRequestFormValuesFromState.categories[storageGroup.category].width) : undefined,
            length: isCustomArea ? convertToNumber(bookingRequestFormValuesFromState.categories[storageGroup.category].length) : undefined,
            tenantOrganizationType: undefined // TODO
        };
    };

    const areaAndPriceResult = getAreaAndPrice(storageSite, sanitizedSelectedCategory, bookingRequestFormValuesFromState.requestedStartDate, selectedActor?.organizationType, appContext);

    const [initialPaymentDialogOpen, setInitialPaymentDialogOpen] = useState(false);
    const [savedBooking, setSavedBooking] = useState(undefined);

    const [errors, setErrors] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // use useState to load only once
    const [initialFormValues] = useState(getInitialBookingFormValues(storageSite, bookingRequestFormValuesFromState.requestedStartDate, bookingRequestFormValuesFromState.requestedEndDate, bookingRequestFormValuesFromState.categories, sanitizedSelectedCategory, getSelectedStorageGroupId(bookingRequestFormValuesFromState), appContext, isPreview));
    const [requestDialogOpen, setRequestDialogOpen] = useState(requestDialogOpenFromQuerystring && !!bookingRequestFormValuesAtLoginRedirect);

    const handleBookingFormSubmit = values => {
        // storeBookingRequestValues stores in Redux state so it can be picked up by BookingRequestDialog through getDataForBookingRequestDialog
        // also, some data is persisted in local storage so it can be pre-filled next time the user comes back
        dispatch(storeBookingRequestValues(values));

        const validationErrors = validateBookingFormValues(storageSite, values, appContext, authenticationContext);
        setErrors(validationErrors);
        if(validationErrors.length === 0) {
            if(!selectedActor) {
                // not logged in, redirect
                dispatch(storeBookingRequestValuesAtLoginRedirect(values)); // we wish to keep the values when coming back after logging in
                handleLogIn({ requestDialogOpen: true }, strings.loginRequiredForBookingRequestBody);
            } else {
                // open the booking request dialog where the actor provides additional booking information
                setRequestDialogOpen(true);
            }
        }
    };

    const handleBookingFormValuesChange = values => {
        // storeBookingRequestValues stores in Redux state so it can be picked up by BookingRequestDialog through getDataForBookingRequestDialog
        // also, some data is persisted in local storage so it can be pre-filled next time the user comes back
        dispatch(storeBookingRequestValues(values));
    };

    const handleInitialPaymentDialogOk = ({ values, onCompleted }) => {
        const googleTagManagerMetadata = `${savedBooking.storageGroup.category}-${(savedBooking.subscriptionBooking ? 'subscription' : 'period')}`;
        dispatch(createGoogleTagManagerUserAction(googleTagManagerConstants.userActions.bookingPaymentInitialized, googleTagManagerMetadata));
        makePayment({
            payment: values.payment,
            dispatch,
            booking: savedBooking,
            type: savedBooking.subscriptionBooking ? purchaseTypes.subscriptionBookingInitialPurchase.key : purchaseTypes.periodBookingPurchase.key,
            successUrlParameters: {
                action: googleTagManagerConstants.userActions.bookingPaymentSucceeded
            },
            onFailure: response => {
                setInitialPaymentDialogOpen(false);
                onCompleted();
                dispatch(showErrorDialog(enhanceStripeErrorMessage(response), { title: strings.paymentFailedTitle, body: strings.paymentFailedBody }));
            }
        });
    };

    const handleInitialPaymentDialogCancel = () => {
        setInitialPaymentDialogOpen(false);
    };

    const handleBookingSuccess = booking => {
        setSavedBooking(booking);
        setRequestDialogOpen(false);

        // pay immediately or later?
        if(booking.immediateBookingRequestId) {
            setInitialPaymentDialogOpen(true);
        } else {
            dispatch(showSuccessDialog({
                title: strings.bookingRequestSent,
                body: strings.bookingRequestDescription,
                useAnimation: true,
                closeAction: routes.account.tenantBookingDetails.replace(':bookingId', booking.id)
            }));
        }
    };

    const handleLogIn = (params, message) => {
        const q = queryString.stringify(params);
        dispatch(setRedirectUrl(location.pathname + (q ? '?' + q : '')));
        dispatch(setLogInPageMessage({ title: strings.loginRequired, body: message }));
        history.push(routes.logIn);
    };

    const handleLogInAsOwner = () => {
        dispatch(logIn({ impersonationActorId: storageSite.ownerActor.id }))
        .then(handleResponse(
            () => history.push(routes.account.storageSites)
        ));
    };

    const getDiscountDescription = () => {
        let description = strings.formatString(strings.discountedMonthsTermsAndConditions, getDiscountMinimumBookingLength(storageSite));
        if(!isActorEligibleForDiscount(storageSite)) {
            description += ' ' + strings.discountedMonthsTermsAndConditionsDoesNotApplyToActor;
        }
        return description;
    };

    return (
        <>
            <PageTitle>{storageSite.title}</PageTitle>
            {
                storageSite.images[0] &&
                (
                    <OpenGraphImage
                        url={getResizedImageUrl(storageSite.images[0].mediaUrl, imageOptions)}
                        width={imageOptions.width}
                        height={imageOptions.height}
                        alt={storageSite.title}
                    />
                )
            }
            <StorageSiteFacebookMetaTags storageSite={storageSite} />

            <Helmet defer={false}>
                <meta name="description" content={storageSite.description}/>
            </Helmet>

            <FullScreenLoader open={isSaving} />

            <Container smMaxWidth="585px" withPageMargins className={isPreview ? classes.previewContainer : ''}>
                <StorageSiteDetailsTitle storageSite={storageSite} />
                <StorageSiteBreadcrumbs storageSite={storageSite} storageGroupCategory={sanitizedSelectedCategory} />
                {
                    storageSite.status !== storageSiteStatuses.approved.key &&
                    (
                        <Alert severity="warning">{strings.storageSiteNotApproved}</Alert>
                    )
                }
                <Box className={classes.infoWrapper}>
                    <Box className={classes.infoContainer}>
                        <Box className={classes.imageContainer}>
                            {imageDisplay}
                            <AttentionPolygon text={storageSite.attentionText}/>
                        </Box>
                        <Box className={classes.priceContainer}>
                            <AreaAndPrice
                                storageSite={storageSite}
                                areaAndPriceResult={areaAndPriceResult}
                                horizontal
                            />
                        </Box>
                        <Box>
                            {
                                authenticationContext.isSuperAdminOrImpersonated && storageSite.ownerActor &&
                                (
                                    <Box className={classes.ownerName} component="span">{strings.owner}: {getBusinessOrPrivateName(storageSite.ownerActor)}</Box>
                                )
                            }
                            <Description title={strings.description} titleVariant="h2" html={storageSite.description} marginBottom={4} />
                            {
                                hasDiscountedMonths(storageSite) && <Description title={storageSite.attentionText} titleVariant="h3" html={getDiscountDescription()} marginBottom={4} />
                            }

                            {
                                storageSite.ownerActor?.description &&
                                (
                                    <Description titleVariant="h3" title={strings.ownerDescription} html={storageSite.ownerActor?.description} marginBottom={4} />
                                )
                            }
                            <Facilities storageSite={storageSite} marginBottom={4} />
                            <DimensionsAndFacts storageSite={storageSite} items={areaAndPriceResult.items} marginBottom={4}/>
                            <Address storageSite={storageSite} />
                            <AllStorageSitesForLocation storageSite={storageSite} storageGroupCategory={sanitizedSelectedCategory} />
                            <OpeningTimeSpanRules show={storageSite.openingTimeSpanRules?.length} storageSite={storageSite} />
                            <DocumentsAndServices storageSite={storageSite} />
                            <Box className={classes.actionButtonContainer}>
                                <Hidden mdUp>
                                    <Button
                                        type="button"
                                        color="primary"
                                        variant="contained"
                                        fullWidth
                                        onClick={() => setModalOpen(prev => !prev)}
                                    >
                                        {strings.seePriceAndSendBooking}
                                    </Button>
                                </Hidden>
                                <ContactOwner storageSite={storageSite} userId={user?.id} actorId={selectedActor?.id} setIsSaving={setIsSaving} onLogIn={handleLogIn}/>
                                {
                                    authenticationContext.isSuperAdminOrImpersonated &&
                                    (
                                        <Button
                                            type="button"
                                            color="primary"
                                            variant="contained"
                                            fullWidth={isMobile}
                                            onClick={handleLogInAsOwner}
                                        >
                                            {strings.logInAsOwner}
                                        </Button>
                                    )
                                }
                            </Box>
                            <StorageSiteMap storageSite={storageSite} />
                        </Box>
                    </Box>

                    { /* booking form for desktop: shown inline */ }
                    <Hidden smDown>
                        <Box className={classes.bookingFormContainer}>
                            <BookingForm
                                storageSite={storageSite}
                                selectedCategory={sanitizedSelectedCategory}
                                initialValues={initialFormValues}
                                onSubmit={handleBookingFormSubmit}
                                onValuesChange={handleBookingFormValuesChange}
                                errors={errors}
                                isPreview={isPreview}
                            />
                        </Box>
                    </Hidden>
                </Box>
                { requestDialogOpen && <BookingRequestDialog open onClose={() => setRequestDialogOpen(false)} onBookingSuccess={handleBookingSuccess} storageSite={storageSite} bookingRequestData={getDataForBookingRequestDialog()}/> }
                { initialPaymentDialogOpen && <InitialPaymentDialog booking={savedBooking} open onOk={handleInitialPaymentDialogOk} onCancel={handleInitialPaymentDialogCancel} /> }

            </Container>

            { /* booking form for mobile: shown in a modal */ }
            <Hidden mdUp>
                <Box className={classes.openBookingFormModalButtonContainer}>
                    <Button
                        onClick={() => setModalOpen(prev => !prev)}
                        color="primary"
                        variant="contained"
                        endIcon={<KeyboardArrowUp style={{ fontSize: 22 }} />}
                    >
                        {strings.seePriceAndSendBooking}
                    </Button>
                </Box>

                <Modal
                    open={modalOpen}
                    onClose={() => setModalOpen(prev => !prev)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    style={{ overflow: 'scroll' }}
                    className={classes.bookingModal}
                >
                    <Box>
                        <BookingForm
                            storageSite={storageSite}
                            selectedCategory={sanitizedSelectedCategory}
                            initialValues={initialFormValues}
                            onSubmit={handleBookingFormSubmit}
                            onValuesChange={handleBookingFormValuesChange}
                            errors={errors}
                            isPreview={isPreview}
                            closeModal={() => setModalOpen(false)}
                        />
                    </Box>
                </Modal>
            </Hidden>
        </>
    );
};

StorageSiteDetails.propTypes = {
    storageSite: PropTypes.object.isRequired,
    selectedCategory: PropTypes.object,
    isPreview: PropTypes.bool
};

export default StorageSiteDetails;
